import type { SupportedLanguage } from '../../../i18n';
import { DEFAULT_LANGUAGE } from '../../../i18n';

import zhCN from './zh-CN';
import zhTW from './zh-TW';
import en from './en';
import ja from './ja';
import ko from './ko';

const FALLBACK_LANGUAGE: SupportedLanguage = 'en';

export const friendsTranslations = {
  'zh-CN': zhCN,
  'zh-TW': zhTW,
  en,
  ja,
  ko,
} as const;

export type FriendsTranslations = typeof friendsTranslations;
export type FriendsTranslationDictionary = FriendsTranslations[keyof FriendsTranslations];

export const FRIENDS_PRESET_TAG_KEYS = [
  'presetTagCoworker',
  'presetTagClassmate',
  'presetTagNeighbor',
  'presetTagRoommate',
  'presetTagFamily',
  'presetTagGymBuddy',
  'presetTagGameBuddy',
  'presetTagLunchBuddy',
  'presetTagTravelBuddy',
  'presetTagStudyBuddy',
  'presetTagWorkPartner',
] as const;

export type FriendsPresetTagKey = (typeof FRIENDS_PRESET_TAG_KEYS)[number];

export const getFriendsTranslationDictionary = (
  language: SupportedLanguage,
): FriendsTranslationDictionary => {
  const dictionary = friendsTranslations[language as keyof FriendsTranslations];
  if (dictionary) {
    return dictionary;
  }

  return (
    friendsTranslations[DEFAULT_LANGUAGE as keyof FriendsTranslations] ??
    friendsTranslations[FALLBACK_LANGUAGE]
  );
};

const stripFriendsPrefix = (key: string) => {
  if (key.startsWith('friends.')) {
    return key.substring('friends.'.length);
  }

  return key;
};

const resolveFriendsTranslationValue = (dictionary: Record<string, any>, key: string) => {
  const path = stripFriendsPrefix(key).split('.');
  let value: any = dictionary.friends ?? dictionary;

  for (const segment of path) {
    if (value && typeof value === 'object' && segment in value) {
      value = value[segment];
    } else {
      return undefined;
    }
  }

  return value;
};

const applyFriendsTemplate = (template: string, params?: Record<string, any>) => {
  if (!params) {
    return template;
  }

  return Object.entries(params).reduce((result, [token, value]) => {
    const placeholder = `{${token}}`;
    if (!result.includes(placeholder)) {
      return result;
    }

    return result.split(placeholder).join(String(value));
  }, template);
};

export const translateFriends = (
  language: SupportedLanguage,
  key: string,
  params?: Record<string, any>,
): string => {
  const dictionary = getFriendsTranslationDictionary(language);
  const value = resolveFriendsTranslationValue(dictionary, key);

  if (typeof value !== 'string') {
    return key;
  }

  return applyFriendsTemplate(value, params);
};

export const getTranslatedFriendPresetTags = (
  language: SupportedLanguage,
): string[] => {
  return FRIENDS_PRESET_TAG_KEYS.map((tagKey) => translateFriends(language, `friends.${tagKey}`));
};

export default friendsTranslations;
